import { CountrySwitch, CountrySwitchContext, HeaderBar, IconLinksBar, NavigationBar, ThemeVariables } from '@cms/ui';
import { memo, useContext, useMemo } from 'react';
import { useCartArticleCount } from './hooks/useCartArticleCount';
import { useRouter } from 'next/router';
import { EnvironmentName, getLocale } from '@hub/libs/constants';
import { getLocaleWithFallbackRegion } from '@hub/libs/i18n.helper';
import Locale from '@formatjs/intl-locale';
import { useSessionStore } from '@hub/hooks/states/useSessionStore';
import type { NextRuntimeConfiguration } from '@hub/libs/config';

import '@cms/ui/src/styling/igus-icons.css';
import '@cms/ui/src/styling/theme.css';
import '@cms/ui/src/styling/header-cms-ui.css';
import '@cms/ui/dist/index.css';

import getConfig from 'next/config';
import { LocaleContext, getLocaleForContext } from '@icommerce/i18n';
import type { LinkData } from '@cms/ui/dist/src/components/molecules/countrySwitch/CountrySwitch.types';
import type { LanguageAlternate } from '@hub/models/data';
import type { HeaderProps } from '@hub/layout/Header/types';

export const Header = memo(({ header, translations, default_form_slug, languageAlternates }: HeaderProps) => {
  const cartArticleCount = useCartArticleCount();
  const router = useRouter();
  const { locale } = router;
  const storyblokLocale = getLocaleForContext(locale, LocaleContext.Storyblok);
  const { region } = useMemo(() => new Locale(getLocaleWithFallbackRegion(getLocale({ locale }))), [locale]);
  const session = useSessionStore(state => state.session);
  const { setIsModalOpen } = useContext(CountrySwitchContext);

  // If there is no session, the countryFromSession would be undefined. This would cause the DeliveryLocationNotification to always be rendered on the first visit because undefined is treated the same as a different country.
  // To avoid this, we set the countryFromSession to the region obtained from the locale, which is derived from the next router so that locale and countryFromSession are the "same" on the first visit until the session is available.
  const countryFromSession = session ? session.userCountry : region;

  const { publicRuntimeConfig } = getConfig() as NextRuntimeConfiguration;

  const languageAlternatesForCountrySwitch: LinkData[] = languageAlternates
    .filter((la: LanguageAlternate) => la.hrefLang !== 'x-default')
    .map((la: LanguageAlternate) => ({
      full_slug: la.href,
      locale: getLocaleForContext(la.hrefLang, LocaleContext.Storyblok),
      published: true,
    }));

  const fullUrl = typeof window !== 'undefined' ? window.location.href : router.asPath; // fallback during SSR

  const internationalLinkEn = languageAlternates.find(la => la.locale === 'en')?.href;
  const internationalLinkFr = languageAlternates.find(la => la.locale === 'fr')?.href;
  const internationalLinkEs = languageAlternates.find(la => la.locale === 'es')?.href;

  let isProd: string | null;

  switch (publicRuntimeConfig.envName) {
    case EnvironmentName.ORANGE:
      isProd = 'www';
      break;
    case EnvironmentName.WHITE:
      isProd = 'webtest';
      break;
    default:
      isProd = null;
  }

  return (
    <>
      <ThemeVariables />
      <section id="country-switch" />
      <header>
        {header?.links && header.links.length > 0 && (
          <IconLinksBar links={header.links} background_color={header.background_color} />
        )}
        <HeaderBar
          searchBarPlaceholder={translations.HEADER_NAV_SEARCH}
          shoppingCartName={translations.HEADER_NAV_SHOPPINGCART}
          shoppingCartValue={cartArticleCount}
          with_country_switch
          openCountrySwitchModal={setIsModalOpen}
          country={countryFromSession.toLocaleLowerCase() || 'de'}
          myIgusDomain="/myigus"
          router={router}
          profileText={translations.MY_IGUS_LOGIN}
          locale={storyblokLocale}
        />
        {header?.navigation && !!header.navigation.length && (
          <NavigationBar
            with_contact={header.with_contact}
            defaultFormSlug={default_form_slug}
            country={storyblokLocale}
            select_contact_view={header.select_contact_view}
            contact_link={header.contact_link}
            navigation={header.navigation}
            openCountrySwitchModal={setIsModalOpen}
            with_country_switch
            myContactLabel={translations.MY_CONTACT_LINK_LABEL}
            selectLocationText={translations.SELECT_LOCATION}
            selectPlaceholderText={translations.SELECT_PLACEHOLDER}
            noContactFoundText={translations.CONTACT_PERSON_NO_CONTACTS_FOUND}
            changeLocationText={translations.CHANGE_LOCATION}
            sendMessageText={translations.CONTACT_PERSON_SEND_MESSAGE}
            locationDescriptionText={translations.LOCATION_DESCRIPTION}
            zipCodePlaceholderText={translations.ZIP_CODE_PLACEHOLDER}
            locationErrorText={translations.LOCATION_ERROR}
            findOutMoreText={translations.FIND_OUT_MORE}
            overViewLinkText={translations.HEADER_OVERVIEW}
            middlewareBaseURL={publicRuntimeConfig.storyblokApi.url}
            countrySwitchNav={translations.COUNTRY_SWITCHER_NAVIGATION}
            locale={storyblokLocale}
          />
        )}
      </header>
      <CountrySwitch
        alternateLinksForCountrySwitch={languageAlternatesForCountrySwitch}
        countryOrRegionText={translations.COUNTRY_OR_REGION}
        notificationText={translations.COUNTRY_SELECT_NOTIFICATION_TEXT}
        languageSettingsHeadline={translations.COUNTRY_SELECT_LANGUAGE_SETTINGS_HEADLINE}
        requestLanguageChangeText={translations.COUNTRY_SELECT_REQUEST_LANG_CHANGE_TEXT}
        linkTitle={translations.COUNTRY_SELECT_LINK_TITLE}
        unavailableLinksText={translations.COUNTRY_SELECT_UNAVAILABLE_LINKS_TEXT}
        unavailablePageText={translations.COUNTRY_SELECT_UNAVAILABLE_PAGE_TEXT}
        internationalLinksText={translations.COUNTRY_SELECT_AVAILABLE_INTERNATIONAL_LINKS}
        switchToCountryHomepageText={translations.COUNTRY_SELECT_SWITCH_COUNTRY_TEXT}
        cancelButtonText={translations.COUNTRY_SELECT_CANCEL_BUTTON_TEXT}
        okayButtonText={translations.COUNTRY_SELECT_OKAY_BUTTON_TEXT}
        inputPlaceholderText={translations.COUNTRY_SELECT_INPUT_PLACEHOLDER_TEXT}
        internationalLinkEn={internationalLinkEn}
        internationalLinkTitleEn={translations.COUNTRY_SELECT_INTERNATIONAL_LINK_TITLE_EN}
        internationalLinkTextEn={translations.COUNTRY_SELECT_INTERNATIONAL_LINK_TEXT_EN}
        internationalLinkFr={internationalLinkFr}
        internationalLinkTitleFr={translations.COUNTRY_SELECT_INTERNATIONAL_LINK_TITLE_FR}
        internationalLinkTextFr={translations.COUNTRY_SELECT_INTERNATIONAL_LINK_TEXT_FR}
        internationalLinkEs={internationalLinkEs}
        internationalLinkTitleEs={translations.COUNTRY_SELECT_INTERNATIONAL_LINK_TITLE_ES}
        internationalLinkTextEs={translations.COUNTRY_SELECT_INTERNATIONAL_LINK_TITLE_ES}
        homepageText={translations.COUNTRY_SELECT_HOMEPAGE_TITLE}
        locale={storyblokLocale}
        urlPath={fullUrl}
        isProd={isProd}
      />
    </>
  );
});
